import { MiscRelationsTabModel } from "./../models/tabs/misc-relations";
import { Container } from "aurelia-dependency-injection";
import { TemplateManager } from "./../../shared/helpers/template-manager";
import { TemplateModel } from "./../../shared/helpers/template-model";
import { FulltextTabModel } from "./../models/tabs/fulltext";
import { CICListTabModel } from "./../models/tabs/cic-list-tab";
import { CiteLineListTabModel } from "./../models/tabs/citeline-list-tab";
import { JudgmentTabModel } from "./../models/tabs/judgment";
import { CitationsAndSourcesTabModel } from "./../models/tabs/citations-and-sources";
import { ReportTabModel } from "./../models/tabs/report";
import { ReportTabView } from "./../views/tabs/report";
import { UrlHelper } from "./../../shared/helpers/url-helpers";
import { IDate } from "./../../processing/models/batch-list";
import { IDocumentHolderUI } from "./../interfaces/document-holder-ui";
import { GenericTabModel } from "./../models/tabs/generic-tab";
import { ReportDetailModel } from "./../models/report-detail";
import { ListTabModel } from "./../models/tabs/list-tab";
import { IDocumentModel } from "./../interfaces/document-model";
import { ITabDefinition } from "./../interfaces/tab-definition";

export class TabFactory {

    public static tm: TemplateManager = new TemplateManager(null);

    public static GetAnalysisTabs(documentModel: IDocumentModel): Promise<GenericTabModel[]> {
        const promises = [];
        for (const tabDefinition of documentModel.TabDefinitions) {
            if (tabDefinition.PanelName.toLowerCase() === "analysis") {
                promises.push(this.buildTabModel(tabDefinition, documentModel)); // Get promises of each tab
            }
        }
        return Promise.all(promises).then((tabs) => { // return one promise that resolves when all others do
            return tabs;
        });
    }
    public static GetFullTextTabs(documentModel: IDocumentModel): Promise<GenericTabModel[]> {
        return new Promise<any>((resolve) => {

            documentModel.loadFullText().then(() => {
                const promises = TabFactory.buildFullTextTabs(documentModel);
                resolve(Promise.all(promises).then((tabs) => {
                    tabs = _.sortBy(tabs, (t) => t.Identifier === "fulltext-sources" ? 1 : 0); // Put Citations and sources tab last
                    return tabs;
                }));
            }).catch((err) => {
                resolve([]);
            });
        });
    }

    private static buildFullTextTabs(document: IDocumentModel): Array<Promise<GenericTabModel>> {
        let promises = [];
        for (const tabDefinition of document.TabDefinitions) {
            if (tabDefinition.PanelName.toLowerCase() === "fulltext") {
                promises.push(this.buildTabModel(tabDefinition, document)); // Get promises of each tab
            }
        }
        if (document.ReportDetails) {
            promises = promises.concat(this.buildReportTabs(document));
        }
        return promises;
    }

    private static loadTemplates(def: ITabDefinition, document: IDocumentModel, tab: GenericTabModel): Promise<GenericTabModel> {
        return new Promise((resolve, reject) => {
            TabFactory.tm.getTemplate(def.TemplateName, document.DocumentAccessSku).then((tmp) => {
                tab.Template = tmp.Raw;
                if (def.ChildView && def.ChildView.ViewTemplateName) {
                    TabFactory.tm.getTemplate(def.ChildView.ViewTemplateName, document.DocumentAccessSku).then((childTemplate) => {
                        tab.ChildTemplate = childTemplate.Raw;
                        resolve(tab);
                    });
                } else {
                    resolve(tab);
                }
            });
        });
    }

    private static buildReportTabs(document: IDocumentModel): Array<Promise<ReportTabModel>> {
        const details = document.ReportDetails;
        if (!details) {
            return [];
        }

        const sortedDetails = this.sortByReportRank(document);
        const uniqueSeries = Object.keys(sortedDetails.countBy((d) => d.ReportName));
        const tabs = [];

        for (const seriesName of uniqueSeries) {
            const reports = sortedDetails.where((d) => d.ReportName === seriesName);
            const model = this.buildReportModel(reports, document);
            tabs.push(model);
        }
        return tabs;
    }

    private static sortByReportRank(document: IDocumentModel) {
        document.ParallelCitations.forEach((citation) => {
            const matchedReport = document.ReportDetails.where((d) => d.ReportName === citation.Source.Name);
            if (matchedReport && matchedReport.length > 0) {
                matchedReport[0].Rank = citation.Source.Rank;
            }
        });

        document.ReportDetails.comparator = (rd) => {
            return -rd.get("Rank");
        };

        return document.ReportDetails.sort();
    }

    private static buildReportModel(reports: ReportDetailModel[], docModel: IDocumentModel): Promise<GenericTabModel> {
        return new Promise((resolve, reject) => {
            const report = reports[0];
            const def: ITabDefinition = {
                PanelName: "Fulltext",
                Identifier: report.Source,
                Title: report.ReportName,
                Description: "",
                ViewTypeName: "ReportTabView",
                TemplateName: "report"
            };

            const model = new ReportTabModel(def, docModel, reports);
            this.loadTemplates(def, docModel, model).then((modelWithTemplates) => {
                resolve(modelWithTemplates);
            });
        });
    }

    private static buildTabModel(tabDefinition: ITabDefinition, docModel: IDocumentModel): Promise<GenericTabModel> {
        return new Promise((resolve, reject) => {
            let model;

            switch (tabDefinition.ViewTypeName) {
                case "GenericListTabView":
                case "CategoryDocumentListTabView":
                    model = new ListTabModel(tabDefinition, docModel);
                    break;
                case "CICListTabView":
                    model = new CICListTabModel(tabDefinition, docModel);
                    break;
                case "CiteLineListTabView":
                    model = new CiteLineListTabModel(tabDefinition, docModel);
                    break;
                case "JudgmentTabView":
                    model = new JudgmentTabModel(tabDefinition, docModel);
                    break;
                case "AsEnactedTabView":
                    model = new FulltextTabModel(tabDefinition, docModel);
                    break;
                case "MiscCitedRelationsView":
                    model = new MiscRelationsTabModel(tabDefinition, docModel, this.getMiscCitedCases(docModel));
                    break;
                case "MiscCitingRelationsView":
                    model = new MiscRelationsTabModel(tabDefinition, docModel, this.getMiscCitingCases(docModel));
                    break;
                case "GenericTabView":
                case "LegislationOutlineView":
                default:
                    model = new GenericTabModel(tabDefinition, docModel);
                    break;
            }
            this.loadTemplates(tabDefinition, docModel, model).then((modelWithTemplates) => {
                resolve(modelWithTemplates);
            });

        });

    }

    private static getMiscCitedCases(doc: IDocumentModel) {
        const allCollectionNames = [
            "CitedSectorOneDocuments",
            "CitedSectorTwoDocuments",
            "CitedSectorThreeDocuments",
            "CitedSectorFourDocuments",
            "CitedSectorFiveDocuments",
            "CitedSectorSixDocuments",
            "CitedSectorSevenDocuments",
            "CitedSectorEightDocuments",
            "CitedSectorNineDocuments"
        ];

        const usedCollectionNames = _.pluck(doc.TabDefinitions, "CollectionName");
        const names = _.difference(allCollectionNames, usedCollectionNames);
        return names;
   }
   private static getMiscCitingCases(doc: IDocumentModel) {
    const allCollectionNames = [
        "CitingSectorOneDocuments",
        "CitingSectorTwoDocuments",
        "CitingSectorThreeDocuments",
        "CitingSectorFourDocuments",
        "CitingSectorFiveDocuments",
        "CitingSectorSixDocuments",
        "CitingSectorSevenDocuments",
        "CitingSectorEightDocuments",
        "CitingSectorNineDocuments"
    ];

    const usedCollectionNames = _.pluck(doc.TabDefinitions, "CollectionName");
    return _.difference(allCollectionNames, usedCollectionNames);
}

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/helpers/tab-factory.ts